/**
 * JWT Token 管理器
 * 提供 token 存取、過期檢查、自動刷新等功能
 */
class JWTManager {
  // Token 刷新的緩衝時間（提前 5 分鐘刷新）
  static REFRESH_BUFFER_MS = 5 * 60 * 1000;
  
  // 是否正在刷新 token
  static _isRefreshing = false;
  
  // 刷新 token 的 Promise（用於避免重複刷新）
  static _refreshPromise = null;
  
  // 自動刷新定時器
  static _refreshTimer = null;

  /**
   * 設置 tokens
   */
  static setToken(payload) {
    sessionStorage.setItem('access_token', payload.access_token);
    sessionStorage.setItem('refresh_token', payload.refresh_token);
    
    if (payload.expires_in) {
      const expiryTime = new Date().getTime() + (payload.expires_in * 1000);
      sessionStorage.setItem('token_expiry', expiryTime);
      
      // 設置自動刷新
      this.scheduleTokenRefresh(payload.expires_in * 1000);
    }
  }

  /**
   * 更新 access token
   */
  static updateAccessToken(accessToken, expiresIn) {
    sessionStorage.setItem('access_token', accessToken);
    
    if (expiresIn) {
      const expiryTime = new Date().getTime() + (expiresIn * 1000);
      sessionStorage.setItem('token_expiry', expiryTime);
      
      // 重新設置自動刷新
      this.scheduleTokenRefresh(expiresIn * 1000);
    }
  }

  static getAccessToken() {
    return sessionStorage.getItem('access_token');
  }

  static getRefreshToken() {
    return sessionStorage.getItem('refresh_token');
  }

  static getTokenExpiry() {
    const expiry = sessionStorage.getItem('token_expiry');
    return expiry ? parseInt(expiry) : null;
  }

  /**
   * 檢查 token 是否已過期
   */
  static isTokenExpired() {
    const expiry = this.getTokenExpiry();
    if (!expiry) return true;
    return new Date().getTime() > expiry;
  }

  /**
   * 檢查 token 是否即將過期（在緩衝時間內）
   */
  static isTokenExpiringSoon() {
    const expiry = this.getTokenExpiry();
    if (!expiry) return true;
    return new Date().getTime() > (expiry - this.REFRESH_BUFFER_MS);
  }

  /**
   * 獲取剩餘有效時間（毫秒）
   */
  static getTimeUntilExpiry() {
    const expiry = this.getTokenExpiry();
    if (!expiry) return 0;
    return Math.max(0, expiry - new Date().getTime());
  }

  static hasValidToken() {
    return this.getAccessToken() && !this.isTokenExpired();
  }

  static clearToken() {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('token_expiry');
    
    // 清除自動刷新定時器
    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer);
      this._refreshTimer = null;
    }
  }

  static getAuthHeader() {
    const token = this.getAccessToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  /**
   * 設置自動刷新定時器
   * @param {number} expiresInMs - Token 有效期（毫秒）
   */
  static scheduleTokenRefresh(expiresInMs) {
    // 清除現有定時器
    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer);
    }
    
    // 在過期前 5 分鐘刷新
    const refreshTime = expiresInMs - this.REFRESH_BUFFER_MS;
    
    if (refreshTime > 0) {
      console.log(`[JWTManager] Scheduled token refresh in ${Math.round(refreshTime / 1000 / 60)} minutes`);
      
      this._refreshTimer = setTimeout(async () => {
        console.log('[JWTManager] Auto-refreshing token...');
        try {
          await this.refreshToken();
          console.log('[JWTManager] Token refreshed successfully');
        } catch (error) {
          console.error('[JWTManager] Auto-refresh failed:', error);
          // 刷新失敗，通知用戶重新登入
          window.dispatchEvent(new CustomEvent('tokenExpired'));
        }
      }, refreshTime);
    }
  }

  /**
   * 刷新 access token
   * 使用鎖機制避免並發刷新
   */
  static async refreshToken() {
    // 如果已經在刷新中，返回現有的 Promise
    if (this._isRefreshing && this._refreshPromise) {
      return this._refreshPromise;
    }
    
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    this._isRefreshing = true;
    
    this._refreshPromise = (async () => {
      try {
        const response = await fetch('/api/refresh', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${refreshToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        const data = await response.json();
        
        if (data.code === 1 && data.access_token) {
          this.updateAccessToken(data.access_token, data.expires_in || 7200);
          return true;
        } else {
          throw new Error(data.msg || 'Token refresh failed');
        }
      } finally {
        this._isRefreshing = false;
        this._refreshPromise = null;
      }
    })();
    
    return this._refreshPromise;
  }

  /**
   * 確保 token 有效（必要時刷新）
   */
  static async ensureValidToken() {
    if (!this.getAccessToken()) {
      return false;
    }
    
    if (this.isTokenExpiringSoon()) {
      try {
        await this.refreshToken();
        return true;
      } catch (error) {
        console.error('[JWTManager] Failed to refresh token:', error);
        return false;
      }
    }
    
    return !this.isTokenExpired();
  }

  /**
   * 初始化：檢查現有 token 並設置自動刷新
   */
  static initialize() {
    const expiry = this.getTokenExpiry();
    if (expiry && this.getRefreshToken()) {
      const timeUntilExpiry = expiry - new Date().getTime();
      if (timeUntilExpiry > 0) {
        this.scheduleTokenRefresh(timeUntilExpiry);
      } else if (this.getRefreshToken()) {
        // Token 已過期但有 refresh token，嘗試刷新
        this.refreshToken().catch(() => {
          console.log('[JWTManager] Initial refresh failed');
        });
      }
    }
  }
}

// 在模組載入時初始化
if (typeof window !== 'undefined') {
  JWTManager.initialize();
}

export default JWTManager;
